import React, { useState, useEffect, useRef } from 'react';
import { colors, radius, fonts } from '../theme';
import { social } from '../api';

export function SocialPage() {
  const [tab, setTab] = useState<'mode' | 'explore' | 'invites' | 'chat'>('mode');
  const [socialOn, setSocialOn] = useState(false);
  const [activeUsers, setActiveUsers] = useState<any[]>([]);
  const [sent, setSent] = useState<any[]>([]);
  const [received, setReceived] = useState<any[]>([]);
  const [chatInvitation, setChatInvitation] = useState<any>(null);
  const [chatMessages, setChatMessages] = useState<any[]>([]);
  const [msgInput, setMsgInput] = useState('');
  const [msg, setMsg] = useState('');
  const [busy, setBusy] = useState(false);
  const chatEndRef = useRef<HTMLDivElement>(null);

  const refresh = async () => {
    try {
      const [sentR, recvR] = await Promise.all([social.sentInvitations(), social.receivedInvitations()]);
      setSent(sentR.data);
      setReceived(recvR.data);
    } catch {}
  };

  useEffect(() => { refresh(); }, []);

  useEffect(() => {
    if (activeUsers.length === 0) {
      social.activeUsers().then(r => setActiveUsers(r.data)).catch(() => {});
    }
  }, [tab]);

  const toggleSocial = async () => {
    setBusy(true);
    try {
      const r = await social.toggleMode();
      setSocialOn(r.data.social_mode);
      setMsg(r.data.social_mode ? 'Mode social activé !' : 'Mode social désactivé');
    } catch (err: any) {
      setMsg(err.response?.data?.detail || 'Erreur');
    }
    setBusy(false);
    setTimeout(() => setMsg(''), 3000);
  };

  const sendInvite = async (toUserId: number, messageType: string) => {
    setBusy(true);
    try {
      await social.invite({ to_user_id: toUserId, message_type: messageType });
      setMsg('Invitation envoyée !');
      refresh();
    } catch (err: any) {
      setMsg(err.response?.data?.detail || 'Erreur');
    }
    setBusy(false);
    setTimeout(() => setMsg(''), 3000);
  };

  const respondInvite = async (id: number, status: 'accepted' | 'declined') => {
    try {
      await social.respondInvitation(id, status);
      refresh();
    } catch {}
  };

  const openChat = async (invitation: any) => {
    setChatInvitation(invitation);
    try {
      const r = await social.getChat(invitation.id);
      setChatMessages(r.data);
    } catch {}
  };

  const sendChat = async () => {
    if (!msgInput.trim() || !chatInvitation) return;
    try {
      await social.sendChat(chatInvitation.id, msgInput.trim());
      setMsgInput('');
      const r = await social.getChat(chatInvitation.id);
      setChatMessages(r.data);
    } catch {}
  };

  const msgTypes = [
    { value: 'greeting', label: 'Bonjour, puis-je vous offrir un verre ?' },
    { value: 'wine', label: 'Ce vin est exceptionnel, voulez-vous partagé une dégustation ?' },
    { value: 'dinner', label: 'Souhaitez-vous partager cette table ?' },
    { value: 'compliment', label: 'Votre choix est excellent, permettez-moi de vous le dire.' },
  ];

  return (
    <div>
      <h2 style={{ color: colors.gold, fontSize: 24, marginBottom: 8 }}>Social</h2>
      <p style={{ color: colors.textMuted, fontSize: 14, marginBottom: 24 }}>Connectez-vous avec les autres convives</p>

      {msg && <div style={{ color: colors.success, background: `${colors.success}15`, padding: 12, borderRadius: radius.sm, marginBottom: 16, fontSize: 14 }}>{msg}</div>}

      <div style={{ display: 'flex', gap: 8, marginBottom: 24 }}>
        {(['mode', 'explore', 'invites', 'chat'] as const).map(t => (
          <button key={t} onClick={() => setTab(t)} style={tab === t ? tabActive : tabStyle}>
            {t === 'mode' ? '⚡ Mode' : t === 'explore' ? '👥 Explorer' : t === 'invites' ? '📨 Invitations' : '💬 Chat'}
          </button>
        ))}
      </div>

      {/* MODE TAB */}
      {tab === 'mode' && (
        <div style={{ background: colors.card, borderRadius: radius.md, padding: 24, border: `1px solid ${colors.border}`, textAlign: 'center', maxWidth: 400, margin: '0 auto' }}>
          <div style={{ fontSize: 64, marginBottom: 16 }}>{socialOn ? '🟢' : '⚫'}</div>
          <h3 style={{ color: colors.text, fontSize: 20, marginBottom: 8 }}>Mode Social</h3>
          <p style={{ color: colors.textMuted, fontSize: 14, marginBottom: 20 }}>
            {socialOn ? 'Vous êtes visible par les autres convives' : 'Activez le mode pour rencontrer les autres convives'}
          </p>
          <button onClick={toggleSocial} disabled={busy} style={socialOn ? btnDanger : btnPrimary}>
            {busy ? '...' : socialOn ? 'Désactiver' : 'Activer le mode social'}
          </button>
        </div>
      )}

      {/* EXPLORE TAB */}
      {tab === 'explore' && (
        <div>
          {activeUsers.length === 0 ? (
            <p style={{ color: colors.textMuted, textAlign: 'center', padding: 40 }}>Aucun convive en mode social actuellement</p>
          ) : (
            activeUsers.map((u: any) => (
              <div key={u.id} style={{ background: colors.card, borderRadius: radius.md, padding: 16, border: `1px solid ${colors.border}`, marginBottom: 12, display: 'flex', alignItems: 'center', gap: 16 }}>
                <div style={{ width: 48, height: 48, borderRadius: '50%', background: colors.gold, display: 'flex', alignItems: 'center', justifyContent: 'center', color: colors.bg, fontWeight: 'bold', fontSize: 18 }}>
                  {(u.first_name?.[0] || '?').toUpperCase()}
                </div>
                <div style={{ flex: 1 }}>
                  <div style={{ color: colors.text, fontWeight: 600 }}>{u.first_name || 'Anonyme'}</div>
                  <div style={{ color: colors.textMuted, fontSize: 13 }}>Convive</div>
                </div>
                <button onClick={() => sendInvite(u.id, 'greeting')} disabled={busy} style={btnSmall}>
                  Inviter
                </button>
              </div>
            ))
          )}
        </div>
      )}

      {/* INVITATIONS TAB */}
      {tab === 'invites' && (
        <div>
          <h3 style={{ color: colors.text, fontSize: 16, marginBottom: 12 }}>Reçues</h3>
          {received.filter((i: any) => i.status === 'pending').length === 0 ? (
            <p style={{ color: colors.textMuted, fontSize: 14, marginBottom: 20 }}>Aucune invitation en attente</p>
          ) : (
            received.filter((i: any) => i.status === 'pending').map((inv: any) => {
              const msgObj = msgTypes.find(m => m.value === inv.message_type);
              return (
                <div key={inv.id} style={{ background: colors.card, padding: 16, borderRadius: radius.md, border: `1px solid ${colors.border}`, marginBottom: 12 }}>
                  <div style={{ color: colors.text, fontWeight: 600 }}>{inv.from_user_id}</div>
                  <div style={{ color: colors.gold, fontStyle: 'italic', fontSize: 14, marginBottom: 12 }}>"{msgObj?.label || inv.message_text || 'Bonjour'}"</div>
                  <div style={{ display: 'flex', gap: 8 }}>
                    <button onClick={() => respondInvite(inv.id, 'accepted')} style={{ ...btnSmall, background: colors.success, borderColor: colors.success, color: '#fff' }}>Accepter</button>
                    <button onClick={() => respondInvite(inv.id, 'declined')} style={{ ...btnSmall, background: colors.error, borderColor: colors.error, color: '#fff' }}>Décliner</button>
                  </div>
                </div>
              );
            })
          )}
          <h3 style={{ color: colors.text, fontSize: 16, margin: '20px 0 12px' }}>Envoyées</h3>
          {sent.map((inv: any) => (
            <div key={inv.id} style={{ background: colors.card, padding: 12, borderRadius: radius.md, border: `1px solid ${colors.border}`, marginBottom: 8, display: 'flex', justifyContent: 'space-between' }}>
              <span style={{ color: colors.textMuted, fontSize: 14 }}>Invitation #{inv.id}</span>
              <span style={{ color: inv.status === 'accepted' ? colors.success : inv.status === 'declined' ? colors.error : colors.orange, fontSize: 13 }}>{inv.status === 'accepted' ? 'Acceptée' : inv.status === 'declined' ? 'Déclinée' : 'En attente'}</span>
            </div>
          ))}
        </div>
      )}

      {/* CHAT TAB */}
      {tab === 'chat' && (
        <div>
          {!chatInvitation ? (
            <div>
              <h3 style={{ color: colors.text, fontSize: 16, marginBottom: 12 }}>Conversations acceptées</h3>
              {[...received.filter((i: any) => i.status === 'accepted'), ...sent.filter((i: any) => i.status === 'accepted')].map((inv: any) => (
                <div key={inv.id} onClick={() => openChat(inv)} style={{ background: colors.card, padding: 16, borderRadius: radius.md, border: `1px solid ${colors.border}`, marginBottom: 8, cursor: 'pointer' }}>
                  <span style={{ color: colors.text }}>Conversation #{inv.id}</span>
                </div>
              ))}
              {[...received.filter((i: any) => i.status === 'accepted'), ...sent.filter((i: any) => i.status === 'accepted')].length === 0 && (
                <p style={{ color: colors.textMuted, textAlign: 'center', padding: 20 }}>Aucune conversation active</p>
              )}
            </div>
          ) : (
            <div>
              <button onClick={() => setChatInvitation(null)} style={{ background: 'none', border: 'none', color: colors.gold, cursor: 'pointer', marginBottom: 12, fontFamily: fonts.body }}>← Retour</button>
              <div style={{ background: colors.card, borderRadius: radius.md, border: `1px solid ${colors.border}`, height: 400, display: 'flex', flexDirection: 'column' }}>
                <div style={{ flex: 1, overflow: 'auto', padding: 16 }}>
                  {chatMessages.map((m: any) => (
                    <div key={m.id} style={{ marginBottom: 8, display: 'flex', justifyContent: m.sender_id === chatInvitation.from_user_id ? 'flex-start' : 'flex-end' }}>
                      <div style={{ background: m.sender_id === chatInvitation.from_user_id ? colors.inputBg : colors.gold, color: m.sender_id === chatInvitation.from_user_id ? colors.text : colors.bg, padding: '8px 14px', borderRadius: radius.sm, maxWidth: '70%', fontSize: 14 }}>
                        {m.content}
                      </div>
                    </div>
                  ))}
                  <div ref={chatEndRef} />
                </div>
                <div style={{ display: 'flex', borderTop: `1px solid ${colors.border}`, padding: 12 }}>
                  <input value={msgInput} onChange={e => setMsgInput(e.target.value)} onKeyDown={e => e.key === 'Enter' && sendChat()} placeholder="Message..." style={{ flex: 1, ...inputStyle, marginBottom: 0 }} />
                  <button onClick={sendChat} style={{ ...btnPrimary, width: 'auto', marginLeft: 8, padding: '10px 20px' }}>Envoyer</button>
                </div>
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

const inputStyle: React.CSSProperties = {
  padding: '10px 14px', background: colors.inputBg, border: `1px solid ${colors.border}`,
  borderRadius: radius.sm, color: colors.text, fontSize: 14, outline: 'none', fontFamily: fonts.body,
};

const btnPrimary: React.CSSProperties = {
  padding: '12px', background: colors.gold, color: colors.bg, border: 'none',
  borderRadius: radius.sm, fontSize: 15, fontWeight: 'bold', cursor: 'pointer', fontFamily: fonts.body,
};

const btnDanger: React.CSSProperties = {
  ...btnPrimary, background: colors.error,
};

const btnSmall: React.CSSProperties = {
  padding: '6px 14px', background: 'transparent', color: colors.gold,
  border: `1px solid ${colors.gold}`, borderRadius: radius.sm, fontSize: 13, cursor: 'pointer', fontFamily: fonts.body,
};

const tabStyle: React.CSSProperties = {
  padding: '8px 18px', background: colors.card, color: colors.textMuted,
  border: `1px solid ${colors.border}`, borderRadius: 20, cursor: 'pointer', fontSize: 13, fontFamily: fonts.body,
};

const tabActive: React.CSSProperties = {
  ...tabStyle, background: colors.gold, color: colors.bg, border: `1px solid ${colors.gold}`, fontWeight: 'bold',
};